// DEPENDENCIES ---------------------------------------------------------------- //

import React from 'react';
import { gql, useMutation } from '@apollo/client';
import { createGuestrideWithScenario } from '@hopdrive/sdk-dispatch';
import { getContractForService } from '@hopdrive/sdk-ar';
import { PRODUCT_KEY_GUEST_RIDES } from '@hopdrive/sdk-products/catalog';
import { useGuestRideActions } from '../hooks/useGuestRideActions';
import { useGuestRideStore } from '../stores/guestRideStore';
import { useRooftop, useUser, useCustomerConfig } from '@store';
import { toast } from 'react-toastify';
import * as Sentry from '@sentry/react';
import axios from 'axios';
import { authService } from '@features/auth/services/authService';
import config from '@config';
import type { PickedLocation } from '../utils/guestRideUtils';

import { css } from '@emotion/css';
import {
  Alert,
  AlertTitle,
  Box,
  CircularProgress,
  Theme,
  useTheme,
  InputAdornment,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  Button,
} from '@mui/material';
import { Map, useMap } from '@vis.gl/react-google-maps';
import { LocationPinMarker } from '@components/Maps';
import { BiClipboard, BiNotepad, BiPencil, BiSolidInfoCircle, BiSolidMapPin, BiUser, BiMobile } from 'react-icons/bi';

import Modal from '@components/ModalComponents/Modal';
import ModalHeader from '@components/ModalComponents/ModalHeader';
import ModalContent from '@components/ModalComponents/ModalContent';
import ModalFooter from '@components/ModalComponents/ModalFooter';

import GuestRideAddressInput from './GuestRideAddressInput';
import { getRideEstimate, type RideEstimate } from '../services/estimateService';

// GRAPHQL ---------------------------------------------------------------- //

const UPDATE_GUESTRIDE = gql`
  mutation UpdateGuestride($id: bigint!, $set: guestrides_set_input!) {
    update_guestrides_by_pk(pk_columns: { id: $id }, _set: $set) {
      id
    }
  }
`;

// TYPES ---------------------------------------------------------------- //

interface GuestRideModalProps {
  open: boolean;
  onClose: () => void;
  ride?: any;
  /** When true (on a failed ride), saving the edits also re-calls the ride. */
  isRetry?: boolean;
}

type ProductType = 'STANDARD' | 'XL' | 'LUXURY' | 'LUXURY_XL';

const PRODUCT_TYPE_OPTIONS: { value: ProductType; label: string }[] = [
  { value: 'STANDARD', label: 'Standard' },
  { value: 'XL', label: 'XL' },
  { value: 'LUXURY', label: 'Luxury' },
  { value: 'LUXURY_XL', label: 'Luxury XL' },
];

const PRODUCT_TYPE_VALUES = PRODUCT_TYPE_OPTIONS.map(o => o.value) as ProductType[];

interface FormState {
  guest_name: string;
  guest_phone: string;
  productType: ProductType;
  reference_num: string;
  notes: string;
}

const initialFormState: FormState = {
  guest_name: '',
  guest_phone: '',
  productType: 'STANDARD',
  reference_num: '',
  notes: '',
};

const DEFAULT_PROVIDER = 'auto';

const maxNotesLength = 128;

// HELPERS ---------------------------------------------------------------- //

/** Formats a phone number progressively as the user types */
const formatPhoneProgressive = (value: string): string => {
  const raw = value.replace(/\D/g, '');
  const digits = raw.length > 10 && raw.startsWith('1') ? raw.slice(1) : raw;
  const len = digits.length;
  if (len === 0) return '';
  if (len <= 3) return `(${digits}`;
  if (len <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
};

// MAP FIT BOUNDS HELPER -------------------------------------------------------- //

const MapFitBounds: React.FC<{ pickup: PickedLocation | null; dropoff: PickedLocation | null }> = ({
  pickup,
  dropoff,
}) => {
  const map = useMap('guest-ride-modal-map');

  React.useEffect(() => {
    if (!map || !window.google?.maps) return;

    if (pickup && dropoff) {
      const bounds = new window.google.maps.LatLngBounds();
      bounds.extend({ lat: pickup.lat, lng: pickup.lng });
      bounds.extend({ lat: dropoff.lat, lng: dropoff.lng });
      map.fitBounds(bounds, 60);
    } else if (pickup) {
      map.panTo({ lat: pickup.lat, lng: pickup.lng });
      map.setZoom(14);
    } else if (dropoff) {
      map.panTo({ lat: dropoff.lat, lng: dropoff.lng });
      map.setZoom(14);
    }
  }, [map, pickup, dropoff]);

  return null;
};

// PICKUP LOCATION DISPLAY -------------------------------------------------------- //

const PickupLocationDisplay: React.FC<{
  location: { name?: string; address?: string };
  onOverride: () => void;
  styles: ReturnType<typeof useStyles>;
}> = ({ location, onOverride, styles }) => (
  <div>
    <label className={styles.label}>
      Pickup Location
      <span className={styles.labelRequired}> *</span> <span className={styles.labelAuto}>(Auto-set)</span>
    </label>
    <div className={styles.serviceLocationBox}>
      <BiSolidMapPin className={styles.serviceLocationIcon} size={20} />
      <div className={styles.serviceLocationText}>
        <Typography variant='body2' sx={{ fontWeight: 500 }}>
          {location?.name || 'Rooftop Location'}
        </Typography>
        <Typography variant='caption' sx={{ color: 'text.secondary' }}>
          {location?.address || 'No address available'}
        </Typography>
      </div>
      <Tooltip title='Change pickup location' placement='top'>
        <div className={styles.serviceOverrideBtn} onClick={onOverride}>
          <BiPencil size={18} />
        </div>
      </Tooltip>
    </div>
  </div>
);

// COMPONENT ---------------------------------------------------------------- //

const GuestRideModal: React.FC<GuestRideModalProps> = ({ open, onClose, ride, isRetry = false }) => {
  const theme = useTheme();
  const cls = useStyles(theme);

  const rooftop = useRooftop();
  const user = useUser();
  const customerConfig = useCustomerConfig();
  const { refetch, setStatusFilter, setHighlightedRideId } = useGuestRideActions();
  const markRideCalled = useGuestRideStore((s) => s.markRideCalled);

  const isEditing = !!ride;
  const refLabel = customerConfig?.reference_num_label || 'Reference ID';

  const rooftopLocation = rooftop?.location;
  const hasRooftopLocation = !!(rooftopLocation?.address && rooftopLocation?.latitude && rooftopLocation?.longitude);

  const [form, setForm] = React.useState<FormState>(initialFormState);
  const [errors, setErrors] = React.useState<Partial<Record<keyof FormState | 'pickup' | 'dropoff', string>>>({});
  const [pickup, setPickup] = React.useState<PickedLocation | null>(null);
  const [dropoff, setDropoff] = React.useState<PickedLocation | null>(null);
  const [pickupOverride, setPickupOverride] = React.useState(false);

  const [updateGuestride, { loading: updating }] = useMutation(UPDATE_GUESTRIDE);
  const [creating, setCreating] = React.useState(false);
  const [retrying, setRetrying] = React.useState(false);
  const submitting = creating || updating || retrying;

  // Trip distance / serviceability check (gated against the rooftop's max_trip_miles).
  const [estimate, setEstimate] = React.useState<RideEstimate | null>(null);
  const [estimateLoading, setEstimateLoading] = React.useState(false);

  const guestridesConfig =
    (rooftop?.effective_config as { guestrides?: Record<string, unknown> } | undefined)?.guestrides ?? null;

  // Only gate at booking time — when creating a new ride or retrying a failed one.
  const enforceDistance = !isEditing || isRetry;
  const isHardBlocked =
    enforceDistance &&
    !!estimate &&
    !estimate.allowed &&
    (estimate.reason === 'too_far' || estimate.reason === 'not_serviceable');

  const customerId = rooftop?.id || user?.profile?.customer_id;

  const showPickupLocationBlock = hasRooftopLocation && !pickupOverride && pickup !== null;

  // Populate form when modal opens
  React.useEffect(() => {
    if (open && ride) {
      // Edit mode: populate from ride data
      const ridePT = ride.config?.productType;
      setForm({
        guest_name: ride.guest_name || '',
        guest_phone: formatPhoneProgressive(ride.guest_phone || ''),
        productType: PRODUCT_TYPE_VALUES.includes(ridePT) ? (ridePT as ProductType) : 'STANDARD',
        reference_num: ride.config?.reference_num || '',
        notes: ride.notes || '',
      });
      setErrors({});
      setPickupOverride(true);

      if (ride.pickup_address && ride.pickup_lat && ride.pickup_lng) {
        setPickup({ address: ride.pickup_address, lat: Number(ride.pickup_lat), lng: Number(ride.pickup_lng) });
      } else {
        setPickup(null);
      }
      if (ride.dropoff_address && ride.dropoff_lat && ride.dropoff_lng) {
        setDropoff({ address: ride.dropoff_address, lat: Number(ride.dropoff_lat), lng: Number(ride.dropoff_lng) });
      } else {
        setDropoff(null);
      }
    } else if (open && !ride) {
      // Create mode
      setForm(initialFormState);
      setErrors({});
      setDropoff(null);
      setPickupOverride(false);

      if (hasRooftopLocation) {
        setPickup({
          address: rooftopLocation!.address!,
          lat: Number(rooftopLocation!.latitude),
          lng: Number(rooftopLocation!.longitude),
        });
      } else {
        setPickup(null);
      }
    }
  }, [open, ride, hasRooftopLocation, rooftopLocation]);

  // Debounced trip distance check whenever pickup/dropoff change (booking flows only).
  React.useEffect(() => {
    if (!open || !enforceDistance || !pickup || !dropoff) {
      setEstimate(null);
      setEstimateLoading(false);
      return;
    }

    let cancelled = false;
    setEstimateLoading(true);

    const timer = setTimeout(async () => {
      try {
        const result = await getRideEstimate(pickup, dropoff, guestridesConfig);
        if (!cancelled) setEstimate(result);
      } catch (err) {
        // Estimate unavailable (e.g. functions not running in dev, or a transient
        // failure) — allow the booking through rather than blocking on it.
        if (!cancelled) setEstimate(null);
        console.warn('[GuestRideModal] ride distance check unavailable:', err instanceof Error ? err.message : err);
        Sentry.captureException(err, {
          level: 'warning',
          tags: { feature: 'guest-rides', action: 'distance-estimate' },
          extra: { rideId: ride?.id, customerId, pickup, dropoff },
        });
      } finally {
        if (!cancelled) setEstimateLoading(false);
      }
    }, 500);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, enforceDistance, pickup?.lat, pickup?.lng, dropoff?.lat, dropoff?.lng, rooftop?.id]);

  const handleChange = (field: keyof FormState) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setForm(prev => ({ ...prev, [field]: e.target.value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatPhoneProgressive(e.target.value);
    setForm(prev => ({ ...prev, guest_phone: formatted }));
    if (errors.guest_phone) {
      setErrors(prev => ({ ...prev, guest_phone: undefined }));
    }
  };

  const handlePhoneBlur = () => {
    const digits = form.guest_phone.replace(/\D/g, '');
    if (digits.length > 0 && digits.length !== 10) {
      setErrors(prev => ({ ...prev, guest_phone: 'Please enter a valid 10-digit phone number' }));
    }
  };

  const validate = (): boolean => {
    const newErrors: typeof errors = {};
    if (!form.guest_name.trim()) newErrors.guest_name = 'Guest name is required';
    if (!form.guest_phone.trim()) {
      newErrors.guest_phone = 'Phone number is required';
    } else {
      const digits = form.guest_phone.replace(/\D/g, '');
      if (digits.length !== 10) {
        newErrors.guest_phone = 'Please enter a valid 10-digit phone number';
      }
    }
    if (!pickup) newErrors.pickup = 'Pickup location is required';
    if (!dropoff) newErrors.dropoff = 'Dropoff location is required';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const buildConfig = () => ({
    productType: form.productType,
    ...(form.reference_num.trim() ? { reference_num: form.reference_num.trim() } : {}),
  });

  /** Re-calls the ride with the freshly edited details. Returns true on success. */
  const callRide = async (): Promise<boolean> => {
    const token = await authService.getAccessTokenWithAutoRefresh();

    const isDevelopment = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
    const baseUrl = isDevelopment ? window.location.origin : config.hopridesBaseUrl;
    const endpoint = isDevelopment ? `${baseUrl}/.netlify/functions/app-call-ride` : `${baseUrl}/v1/app-call-ride`;

    const response = await axios({
      method: 'POST',
      url: endpoint,
      data: {
        guestrideId: ride?.id,
        provider: DEFAULT_PROVIDER,
        productType: form.productType,
      },
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    });

    return response?.data?.success === true;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    if (isHardBlocked) return;

    const cleanPhone = form.guest_phone.replace(/\D/g, '');

    try {
      if (isEditing) {
        await updateGuestride({
          variables: {
            id: ride.id,
            set: {
              guest_name: form.guest_name.trim(),
              guest_phone: cleanPhone,
              pickup_address: pickup!.address,
              pickup_lat: pickup!.lat,
              pickup_lng: pickup!.lng,
              dropoff_address: dropoff!.address,
              dropoff_lat: dropoff!.lat,
              dropoff_lng: dropoff!.lng,
              config: buildConfig(),
              notes: form.notes.trim() || null,
              provider: DEFAULT_PROVIDER,
              updated_by: user?.profile?.email || null,
            },
          },
        });

        if (isRetry) {
          setRetrying(true);
          let success = false;
          try {
            success = await callRide();
          } catch (callErr) {
            console.error('Failed to retry ride:', callErr);
            Sentry.captureException(callErr, {
              tags: { feature: 'guest-rides', action: 'retry-ride' },
              extra: { rideId: ride?.id, status: ride?.status, productType: form.productType },
            });
          } finally {
            setRetrying(false);
          }
          if (success) {
            toast.success('Ride retried successfully! Awaiting response...');
            // Optimistically flip the ride to `pending` so the Retry button
            // doesn't linger and invite a second click while the server catches up.
            if (ride?.id != null) markRideCalled(ride.id, ride.status);
            if (refetch) refetch();
            setHighlightedRideId(ride.id);
            handleClose();
          } else {
            toast.error('Failed to retry ride');
            if (refetch) refetch();
          }
        } else {
          toast.success('Ride updated');
          if (refetch) refetch();
          setHighlightedRideId(ride.id);
          handleClose();
        }
      } else {
        const email = user?.profile?.email || null;
        setCreating(true);
        let result;
        try {
          // Resolve the active guestrides contract → workflowset_id so the
          // guestride row is anchored to the contract AR will look up
          // downstream. Without this, sdk-dispatch falls back to a
          // workflowsets-table walk that can drift from the contract.
          if (typeof customerId !== 'number') {
            toast.error('No customer context — please reload');
            return;
          }
          const contractResult = await getContractForService(customerId, PRODUCT_KEY_GUEST_RIDES);
          if (!contractResult) {
            Sentry.captureMessage('Guest ride creation blocked: no active guestrides contract', {
              level: 'error',
              tags: { feature: 'guest-rides', action: 'create-ride' },
              extra: { customerId },
            });
            toast.error('No active guestrides contract for this customer');
            return;
          }
          if (!contractResult.workflowset) {
            Sentry.captureMessage('Guest ride creation blocked: guestrides contract missing a workflowset', {
              level: 'error',
              tags: { feature: 'guest-rides', action: 'create-ride' },
              extra: { customerId },
            });
            toast.error('Guestrides contract is missing a workflowset');
            return;
          }

          result = await createGuestrideWithScenario({
            user: email || 'system',
            config: buildConfig(),
            guestride: {
              guest_name: form.guest_name.trim(),
              guest_phone: cleanPhone,
              pickup_address: pickup!.address,
              pickup_lat: pickup!.lat,
              pickup_lng: pickup!.lng,
              dropoff_address: dropoff!.address,
              dropoff_lat: dropoff!.lat,
              dropoff_lng: dropoff!.lng,
              config: buildConfig(),
              notes: form.notes.trim() || null,
              provider: DEFAULT_PROVIDER,
              customer_id: customerId,
              workflowset_id: contractResult.workflowset.id,
              status: 'new',
              created_by: email,
              updated_by: email,
            },
          });
        } finally {
          setCreating(false);
        }

        const newId = result?.scenario?.guestrideId;

        if (refetch) refetch();
        setStatusFilter('upcoming');
        if (newId) setHighlightedRideId(newId);

        handleClose();
      }
    } catch (err) {
      console.error(`Failed to ${isEditing ? 'update' : 'create'} guest ride:`, err);
      Sentry.captureException(err, {
        tags: { feature: 'guest-rides', action: isEditing ? 'update-ride' : 'create-ride' },
        extra: { rideId: ride?.id, customerId, isEditing, isRetry },
      });
      toast.error(`Failed to ${isEditing ? 'update' : 'request'} ride. Please try again.`);
    }
  };

  const handleClose = () => {
    setForm(initialFormState);
    setErrors({});
    setPickup(null);
    setDropoff(null);
    setPickupOverride(false);
    setEstimate(null);
    setEstimateLoading(false);
    onClose();
  };

  // Map center / zoom helpers
  const getDefaultCenter = () => {
    if (pickup && dropoff) {
      return { lat: (pickup.lat + dropoff.lat) / 2, lng: (pickup.lng + dropoff.lng) / 2 };
    }
    return pickup
      ? { lat: pickup.lat, lng: pickup.lng }
      : dropoff
        ? { lat: dropoff.lat, lng: dropoff.lng }
        : { lat: 32.7767, lng: -96.797 }; // Dallas fallback
  };

  const getDefaultZoom = () => {
    if (pickup && dropoff) {
      const maxDiff = Math.max(Math.abs(pickup.lat - dropoff.lat), Math.abs(pickup.lng - dropoff.lng));
      if (maxDiff > 0.5) return 10;
      if (maxDiff > 0.2) return 11;
      if (maxDiff > 0.1) return 12;
      if (maxDiff > 0.05) return 13;
      return 14;
    }
    return 14;
  };

  const hasMap = pickup || dropoff;

  const failedReason =
    isEditing && ride?.status === 'failed' ? ride?.ridehails?.[0]?.quality_rejection_reason ?? null : null;

  return (
    <Modal open={open} handleClose={handleClose} width='sm'>
      <ModalHeader handleClose={handleClose}>
        {isRetry ? 'Retry Ride' : isEditing ? 'Edit Ride' : 'Request a Ride'}
      </ModalHeader>

      <ModalContent>
        {/* Failure reason */}
        {failedReason ? (
          <Alert severity='error' variant='outlined' sx={{ mb: 1.5 }}>
            <AlertTitle sx={{ fontSize: 13, fontWeight: 600, mb: 0.25 }}>Ride call failed</AlertTitle>
            <Typography variant='body2' sx={{ lineHeight: 1.3, wordBreak: 'break-word' }}>
              {failedReason}
            </Typography>
          </Alert>
        ) : null}

        {/* Info blurb */}
        <div className={cls.blurb}>
          <BiSolidInfoCircle className={cls.blurbIcon} />
          <Typography variant='body2' sx={{ textWrap: 'pretty', color: theme.palette.text.secondary }}>
            {isRetry
              ? 'Adjust the details below — such as the product type — to get around the failure, then retry the ride.'
              : isEditing
                ? 'Update the details for this guest ride.'
                : 'Fill out the details below to request a ride for your guest. This will not instantly call the ride, but will set up a placeholder for you to call at any time.'}
          </Typography>
        </div>

        {/* Guest info */}
        <div className={cls.flex}>
          <div className={cls.flexItem}>
            <label className={cls.label}>
              Guest Name
              <span className={cls.labelRequired}> *</span>
            </label>
            <TextField
              fullWidth
              placeholder='Enter guest name...'
              value={form.guest_name}
              onChange={handleChange('guest_name')}
              error={!!errors.guest_name}
              helperText={errors.guest_name}
              slotProps={{
                htmlInput: { maxLength: 40 },
                input: {
                  startAdornment: (
                    <InputAdornment position='start'>
                      <BiUser style={{ fontSize: 20 }} />
                    </InputAdornment>
                  ),
                },
              }}
            />
          </div>

          <div className={cls.flexItem}>
            <label className={cls.label}>
              Guest Phone
              <span className={cls.labelRequired}> *</span>
            </label>
            <TextField
              fullWidth
              type='tel'
              placeholder='(555) 555-5555'
              value={form.guest_phone}
              onChange={handlePhoneChange}
              onBlur={handlePhoneBlur}
              error={!!errors.guest_phone}
              helperText={errors.guest_phone}
              slotProps={{
                htmlInput: { maxLength: 14 },
                input: {
                  startAdornment: (
                    <InputAdornment position='start'>
                      <BiMobile style={{ fontSize: 20 }} />
                    </InputAdornment>
                  ),
                },
              }}
            />
          </div>
        </div>

        <div className={cls.spacer} />

        {/* Product type */}
        <label className={cls.label}>
          Product Type
          <span className={cls.labelRequired}> *</span>
        </label>
        <ToggleButtonGroup
          exclusive
          value={form.productType}
          onChange={(_, value: ProductType | null) => {
            if (value !== null) setForm(prev => ({ ...prev, productType: value }));
          }}
          fullWidth
          sx={{ height: 40 }}
        >
          {PRODUCT_TYPE_OPTIONS.map(opt => (
            <ToggleButton key={opt.value} value={opt.value} sx={{ flex: 1 }}>
              <Typography sx={{ fontWeight: 600, fontSize: 14 }}>{opt.label}</Typography>
            </ToggleButton>
          ))}
        </ToggleButtonGroup>

        <div className={cls.spacer} />

        {/* Pickup location */}
        {showPickupLocationBlock ? (
          <PickupLocationDisplay location={rooftopLocation!} onOverride={() => setPickupOverride(true)} styles={cls} />
        ) : (
          <>
            <label className={cls.label}>
              Pickup Location
              <span className={cls.labelRequired}> *</span>
            </label>
            <GuestRideAddressInput
              placeholder='Search for pickup address...'
              value={pickup}
              onChange={loc => {
                setPickup(loc);
                if (loc && errors.pickup) setErrors(prev => ({ ...prev, pickup: undefined }));
              }}
              error={errors.pickup}
            />
          </>
        )}

        <div className={cls.spacer} />

        {/* Dropoff location */}
        <label className={cls.label}>
          Dropoff Location
          <span className={cls.labelRequired}> *</span>
        </label>
        <GuestRideAddressInput
          placeholder='Search for dropoff address...'
          value={dropoff}
          onChange={loc => {
            setDropoff(loc);
            if (loc && errors.dropoff) setErrors(prev => ({ ...prev, dropoff: undefined }));
          }}
          error={errors.dropoff}
        />

        {/* Trip distance check — only surfaced as a real block; a failed/unavailable
            estimate stays silent and lets the booking proceed. */}
        {enforceDistance && pickup && dropoff ? (
          estimateLoading ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1, ml: 0.5 }}>
              <CircularProgress size={14} />
              <Typography variant='caption' sx={{ color: 'text.secondary' }}>
                Checking distance…
              </Typography>
            </Box>
          ) : isHardBlocked ? (
            <Alert severity='error' variant='outlined' sx={{ mt: 1 }}>
              {estimate!.message}
            </Alert>
          ) : null
        ) : null}

        {/* Map preview */}
        {hasMap && (
          <>
            <div className={cls.spacer} />
            <div className={cls.mapContainer}>
              <Map
                id='guest-ride-modal-map'
                mapId='a1bb97db90b89ef9'
                defaultCenter={getDefaultCenter()}
                defaultZoom={getDefaultZoom()}
                disableDefaultUI
                gestureHandling='cooperative'
                clickableIcons={false}
                mapTypeControl={false}
                streetViewControl={false}
                fullscreenControl={false}
                style={{ width: '100%', height: '100%' }}
              >
                <MapFitBounds pickup={pickup} dropoff={dropoff} />
                {pickup && (
                  <LocationPinMarker
                    mapId='guest-ride-modal-map'
                    markerId='pickup'
                    position={{ lat: pickup.lat, lng: pickup.lng }}
                    size={40}
                    iconSize={22}
                  />
                )}
                {dropoff && (
                  <LocationPinMarker
                    mapId='guest-ride-modal-map'
                    markerId='delivery'
                    position={{ lat: dropoff.lat, lng: dropoff.lng }}
                    size={40}
                    iconSize={22}
                  />
                )}
              </Map>
            </div>
          </>
        )}

        <div className={cls.spacer} />

        {/* Reference ID (optional) */}
        <label className={cls.label}>{refLabel}</label>
        <TextField
          fullWidth
          placeholder={`Enter ${refLabel}...`}
          value={form.reference_num}
          onChange={handleChange('reference_num')}
          slotProps={{
            htmlInput: { maxLength: 24 },
            input: {
              startAdornment: (
                <InputAdornment position='start'>
                  <BiClipboard style={{ fontSize: 20 }} />
                </InputAdornment>
              ),
            },
          }}
        />

        <div className={cls.spacer} />

        {/* Notes */}
        <label className={cls.label}>
          Additional Notes{' '}
          <span className={cls.labelLength}>
            ({form.notes.length}/{maxNotesLength})
          </span>
        </label>
        <TextField
          fullWidth
          multiline
          placeholder='Enter any additional notes about this ride...'
          value={form.notes}
          onChange={e => {
            if (e.target.value.length > maxNotesLength) return;
            setForm(prev => ({ ...prev, notes: e.target.value }));
          }}
          slotProps={{
            htmlInput: { maxLength: maxNotesLength },
            input: {
              startAdornment: (
                <InputAdornment position='start'>
                  <BiNotepad style={{ fontSize: 20 }} />
                </InputAdornment>
              ),
            },
          }}
        />

      </ModalContent>

      <ModalFooter>
        <Button disabled={submitting} variant='text' color='utility' onClick={handleClose}>
          Close
        </Button>
        <Button
          disabled={submitting || estimateLoading || isHardBlocked}
          variant='contained'
          color='primary'
          onClick={handleSubmit}
        >
          {submitting
            ? isRetry
              ? 'Retrying...'
              : isEditing
                ? 'Saving...'
                : 'Submitting...'
            : isRetry
              ? 'Retry Ride'
              : isEditing
                ? 'Save Changes'
                : 'Request Ride'}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

// STYLES ---------------------------------------------------------------- //

const useStyles = (theme?: Theme) => {
  return {
    blurb: css`
      display: flex;
      align-items: center;
      gap: 10px;
      padding: 12px;
      border: 1px solid ${theme?.palette?.divider};
      border-radius: 10px;
      margin-bottom: 12px;
      background-color: ${theme?.palette?.action?.hover};
    `,
    blurbIcon: css`
      min-width: 20px;
      min-height: 20px;
      font-size: 20px;
      color: ${theme?.palette?.text?.secondary};
    `,

    label: css`
      display: block;
      margin-bottom: 2px;
      margin-left: 6px;
      font-size: 12px;
      font-weight: 600;
    `,
    labelRequired: css`
      color: ${theme?.palette?.error?.main};
    `,
    labelLength: css`
      color: ${theme?.palette?.text?.secondary};
    `,
    labelAuto: css`
      font-weight: 400;
      color: ${theme?.palette?.text?.secondary};
    `,

    flex: css`
      display: flex;
      align-items: flex-start;
      justify-content: center;
      gap: 12px;
    `,
    flexItem: css`
      flex: 1;
      width: 100%;
    `,

    spacer: css`
      width: 100%;
      height: 12px;
    `,

    mapContainer: css`
      height: 250px;
      border-radius: 8px;
      overflow: hidden;
      border: 1px solid ${theme?.palette?.utility?.lighter};
    `,

    serviceLocationBox: css`
      display: flex;
      align-items: center;
      gap: 12px;
      padding: 14px 16px;
      border: 1px solid ${theme?.palette?.divider};
      border-radius: 4px;
      background-color: ${theme?.palette?.action?.hover};
    `,
    serviceLocationIcon: css`
      color: ${theme?.palette?.text?.secondary};
      flex-shrink: 0;
    `,
    serviceLocationText: css`
      display: flex;
      flex-direction: column;
      min-width: 0;
      flex: 1;
    `,
    serviceOverrideBtn: css`
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 6px;
      border-radius: 4px;
      color: ${theme?.palette?.text?.secondary};
      cursor: pointer;
      transition: 0.15s ease-in-out;
      &:hover {
        background-color: ${theme?.palette?.action?.hover};
        color: ${theme?.palette?.primary?.main};
      }
    `,
  };
};

// EXPORT ---------------------------------------------------------------- //

export default GuestRideModal;
